// noinspection JSUnusedGlobalSymbols

const deepCompareObjects = (obj1, obj2) => {
    if (!isObject(obj1) || !isObject(obj2)) {
        return obj1 === obj2;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (
            !Object.prototype.hasOwnProperty.call(obj2, key) ||
            !deepCompareObjects(obj1[key], obj2[key])
        ) {
            return false;
        }
    }

    return true;
};

const convertToArray = (value) =>
    Array.isArray(value)
        ? filterOutEmptyVals(value)
        : isObject(value)
          ? Object.entries(filterOutEmptyVals(value))
          : filterOutEmptyVals([value]);

const convertToBool = (value) => !isEmpty(value);

const convertToFunction = (value) =>
    Function.isFunction(value) ? value : () => value;

const convertToNumber = (value, options = {}) => {
    options = convertToPlainObject(options, {
        allowBigInt: true,
        onlyFinite: false,
    });
    return isNumber(value, options)
        ? value
        : Array.isArray(value)
          ? 1 >= value.length
              ? Number(value)
              : 0
          : isObject(value, {allowFunctions: true})
            ? 0
            : isNumber(value, {allowBigInt: true, onlyFinite: false})
              ? 0
              : Boolean(value);
};

const convertToPlainObject = (val, defaults = {}, options = {}) => {
    defaults =
        typeof defaults === "object" &&
        null !== defaults &&
        !Array.isArray(defaults)
            ? defaults
            : {};

    const defaultOptions = {
        enforceTypes: false,
        requiredKeys: null,
    };
    options =
        typeof options === "object" &&
        null !== options &&
        !Array.isArray(options)
            ? options
            : {};
    options = {...defaultOptions, ...options};

    let obj = {
        ...defaults,
        ...(isObject(val) ? {...val} : {}),
    };

    if (0 < Object.keys(defaults).length) {
        obj = Object.fromEntries(
            Object.entries(obj).filter(([key]) => key in defaults),
        );

        if (
            null === options.requiredKeys ||
            (Array.isArray(options.requiredKeys) &&
                0 < options.requiredKeys.length)
        ) {
            options.requiredKeys = Array.isArray(options.requiredKeys)
                ? options.requiredKeys
                : Object.keys(defaults);

            for (const key of options.requiredKeys) {
                if (!(key in defaults)) {
                    continue;
                }

                obj[key] = isEmpty(obj[key]) ? defaults[key] : obj[key];
            }
        }
    }

    if (options.enforceTypes) {
        for (const [key, value] of Object.entries(obj)) {
            if (!(key in defaults)) {
                continue;
            }

            const defaultVal = defaults[key];
            switch (true) {
                case Array.isArray(defaultVal):
                    obj[key] = convertToArray(value);
                    break;
                case isObject(defaultVal):
                    obj[key] = convertToPlainObject(value, defaultVal, {
                        enforceTypes: true,
                    });
                    break;
                case "function" === typeof defaultVal:
                    obj[key] = convertToFunction(value);
                    break;
                case "string" === typeof defaultVal:
                    obj[key] = convertToString(value);
                    break;
                case "number" === typeof defaultVal:
                    obj[key] = convertToNumber(value, {
                        allowBigInt: false,
                        onlyFinite: Number.isFinite(defaultVal),
                    });
                    break;
                case "bigint" === typeof defaultVal:
                    obj[key] = BigInt(
                        convertToNumber(value, {onlyFinite: true}),
                    );
                    break;
                case "boolean" === typeof defaultVal:
                    obj[key] = convertToBool(value);
                    break;
                default:
                    break;
            }
        }
    }

    return obj;
};

const convertToString = (val) =>
    "string" === typeof val ||
    (isObject(val) &&
        "function" === typeof val.toString &&
        Object.prototype.toString !== val.toString) ||
    isNumber(val, {onlyFinite: true}) ||
    ("boolean" === typeof val && val)
        ? val.toString()
        : "symbol" === typeof val
          ? val.description
          : "";

const filterOutEmptyVals = (value, fallback = null) =>
    isEmpty(value)
        ? fallback
        : Array.isArray(value)
          ? value.filter((val) => !isEmpty(val)).map(filterOutEmptyVals)
          : isObject(value)
            ? Object.fromEntries(
                  Object.entries(value)
                      .filter(([, val]) => !isEmpty(val))
                      .map(([key, val]) => [key, filterOutEmptyVals(val)]),
              )
            : value;

const getMoment = async () => {
    const {default: moment} = await import("moment");

    if ("function" !== typeof moment) {
        throw new Error("Failed to load moment.js");
    }

    return moment;
};

const isEmpty = (value) =>
    Array.isArray(value)
        ? 0 === value.length
        : isObject(value)
          ? isPlainObject(value)
              ? Object.keys(value).length === 0
              : false
          : !value;

const isNumber = (val, options = {}) => {
    options = convertToPlainObject(options, {
        allowBigInt: true,
        onlyFinite: false,
    });
    const valType = typeof val;

    return (
        [options.allowBigInt ? "bigint" : null, "number"]
            .filter(Boolean)
            .includes(valType) &&
        ((options.allowBigInt && "bigint" === valType) ||
            !options.onlyFinite ||
            Number.isFinite(val))
    );
};

const isObject = (val, options = {}) => {
    const defaultOptions = {
        allowArrays: false,
        allowFunctions: false,
    };
    options =
        typeof options === "object" &&
        null !== options &&
        !Array.isArray(options)
            ? {...defaultOptions, ...options}
            : {};
    return (
        ("object" === typeof val &&
            null !== val &&
            (options.allowArrays || !Array.isArray(val))) ||
        (options.allowFunctions && "function" === typeof val)
    );
};

const isPlainObject = (val) =>
    isObject(val) &&
    [Object.prototype, null].includes(Object.getPrototypeOf(val));
const isRegularNumber = (val) =>
    isNumber(val, {allowBigInt: false, onlyFinite: true});

const loadCSS = (url, el = null, callback = null) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = url;
    link.onerror = () => {
        console.error(`Failed to load CSS: ${url}`);
    };

    if (callback) {
        link.onload = callback;
    }

    el = el || document.head;
    el.appendChild(link);
};

const parseOptions = (options, defaults, requiredKeys = null) => {
    const parsedOptions = convertToPlainObject(options, defaults, {
        enforceTypes: true,
        requiredKeys,
    });
    requiredKeys = Array.isArray(requiredKeys)
        ? requiredKeys
        : Object.keys(defaults);
    const invalidOptions = Object.fromEntries(
        Object.entries(parsedOptions).filter(
            ([key, val]) => isEmpty(val) && requiredKeys.includes(key),
        ),
    );

    return {parsedOptions, invalidOptions};
};

const toggleFocus = (element) => {
    (element === document.activeElement && element.blur()) || element.focus();
};

const validateDateInput = async (date) => {
    const moment = await getMoment();

    return (
        date instanceof moment ||
        "string" === typeof date ||
        isRegularNumber(date) ||
        Array.isArray(date) ||
        isObject(date) ||
        undefined === date
    );
};

export {
    convertToArray,
    convertToBool,
    convertToFunction,
    convertToNumber,
    convertToPlainObject,
    convertToString,
    deepCompareObjects,
    filterOutEmptyVals,
    getMoment,
    isEmpty,
    isNumber,
    isObject,
    isPlainObject,
    isRegularNumber,
    loadCSS,
    parseOptions,
    toggleFocus,
    validateDateInput,
};
